import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CampaignStatusBadge } from './CampaignStatusBadge';

const EVENT_ICON = {
    send: '📤',
    delivery: '✅',
    bounce: '⚠️',
    complaint: '🚫',
    delivery_delay: '⏳',
    rendering_failure: '❌',
    open: '👁️',
    click: '🖱️',
    unknown: '•',
};

function fetchRecipient(campaignId, recipientId) {
    return axios
        .get(`/api/campaigns/${campaignId}/recipients/${recipientId}/`, { withCredentials: true })
        .then((r) => r.data);
}

export default function RecipientDetailPage({ activeLeague }) {
    const { campaignId, recipientId } = useParams();
    const navigate = useNavigate();

    const { data, isLoading, error } = useQuery({
        queryKey: ['recipient-detail', campaignId, recipientId],
        queryFn: () => fetchRecipient(campaignId, recipientId),
    });

    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">Failed to load recipient detail.</div>
                </div>
            </div>
        );
    }

    const r = data?.recipient;
    const events = data?.events || [];

    return (
        <div>
            <button
                className="btn btn-link btn-sm ps-0 mb-3"
                onClick={() => navigate(`/league-manager/campaigns/${campaignId}/`)}
            >
                ← Back to campaign
            </button>

            <div className="card mb-3">
                <div className="card-body">
                    <h6 className="fw-bold mb-3">Recipient</h6>
                    <div className="row g-2 small">
                        <div className="col-md-4">
                            <div className="text-muted">Email</div>
                            <div>{r?.email}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-muted">Name</div>
                            <div>{r?.first_name} {r?.last_name}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-muted">Source</div>
                            <div>{r?.source_display}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-muted">Status</div>
                            <CampaignStatusBadge status={r?.status} />
                        </div>
                        <div className="col-md-4">
                            <div className="text-muted">Sent at</div>
                            <div>{r?.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-muted">Attempts</div>
                            <div>{r?.attempt_count ?? 0}</div>
                        </div>
                        {r?.error_message && (
                            <div className="col-12">
                                <div className="text-muted">Error</div>
                                <div className="text-danger small font-monospace">{r.error_message}</div>
                            </div>
                        )}
                        {r?.skip_reason && (
                            <div className="col-12">
                                <div className="text-muted">Skip reason</div>
                                <div className="small">{r.skip_reason}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <h6 className="fw-bold mb-3">Event timeline</h6>
                    {events.length === 0 ? (
                        <p className="text-muted small">No events recorded yet.</p>
                    ) : (
                        <div className="timeline">
                            {events.map((evt) => (
                                <div key={evt.id} className="d-flex align-items-start mb-3 gap-2">
                                    <div className="mt-1" style={{ fontSize: '1rem', minWidth: 24 }}>
                                        {EVENT_ICON[evt.event_type] || '•'}
                                    </div>
                                    <div>
                                        <div className="fw-semibold small">{evt.event_type_display}</div>
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {evt.event_timestamp
                                                ? new Date(evt.event_timestamp).toLocaleString()
                                                : 'Unknown time'}
                                        </div>
                                        {evt.provider_event_id && (
                                            <div className="text-muted font-monospace" style={{ fontSize: '0.7rem' }}>
                                                {evt.provider_event_id}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
